import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PrimaryButton, SecondaryButton } from '../../../../../components/atoms/Button';
import {
  purchaseDialogActionsSx,
  purchaseDialogContentSx,
  purchaseDialogInnerSx,
  purchaseDialogPaperProps,
  purchaseDialogTitleSx,
  purchaseFieldSx,
} from '../../../purchaseUiStyles';

interface PurchaseLead {
   _id: string;
   quantity: number;
   unitPrice: number;
   status: 'NEW' | 'CONTACTED' | 'FOLLOW_UP' | 'CONVERTED' | 'LOST';
   source: string;
   createdAt: string;
   soldAmount?: number;
   remarks?: string;
   user: {
     name: string;
     phoneNumber: string;
   };
   product: {
     name: string;
     brand: string;
     model: string;
     type?: string;
     tonnage?: number;
     customerPrice?: number;
     modelSeries?: string;
     indoorModelNo?: string;
     outdoorModelNo?: string;
     manufacturer?: string;
   };
 }

interface UpdateLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (updateData: any) => void;
  lead: PurchaseLead | null;
  loading: boolean;
}

const statusOptions = [
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'FOLLOW_UP', label: 'Follow Up' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'LOST', label: 'Lost' },
];

const statusColors = {
  NEW: '#2196f3',
  CONTACTED: '#ff9800',
  FOLLOW_UP: '#9c27b0',
  CONVERTED: '#4caf50',
  LOST: '#f44336',
};

const UpdateLeadDialog: React.FC<UpdateLeadDialogProps> = ({
  open,
  onClose,
  onSubmit,
  lead,
  loading,
}) => {
  const [updateForm, setUpdateForm] = React.useState({
    status: 'NEW' as 'NEW' | 'CONTACTED' | 'FOLLOW_UP' | 'CONVERTED' | 'LOST',
    quantity: 1,
    soldAmount: 0,
    remarks: '',
  });

  React.useEffect(() => {
    if (lead) {
      setUpdateForm({
        status: lead.status,
        quantity: lead.quantity,
        soldAmount: lead.soldAmount || lead.unitPrice * lead.quantity,
        remarks: lead.remarks || '',
      });
    }
  }, [lead]);

  const handleSubmit = () => {
    if (lead) {
      onSubmit(updateForm);
    }
  };

  if (!lead) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={purchaseDialogPaperProps}
    >
      <Box sx={purchaseDialogInnerSx}>
        <DialogTitle sx={purchaseDialogTitleSx}>
          Update Lead
          <IconButton onClick={onClose} size="small" sx={{ color: '#B0BEC5' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={purchaseDialogContentSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                User Information
              </Typography>
              <Box
                sx={{
                  border: '1px solid #333',
                  borderRadius: '8px',
                  p: 2,
                  backgroundColor: '#222',
                }}
              >
                <Typography sx={{ color: '#fff' }}>
                  <strong>Name:</strong> {lead.user.name || 'Unknown'}
                </Typography>
                <Typography sx={{ color: '#fff', mt: 1 }}>
                  <strong>Phone:</strong> {lead.user.phoneNumber}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Status
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={updateForm.status}
                  onChange={(e) =>
                    setUpdateForm({
                      ...updateForm,
                      status: e.target.value as typeof updateForm.status,
                    })
                  }
                  sx={purchaseFieldSx}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor:
                              statusColors[
                                option.value as keyof typeof statusColors
                              ],
                          }}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Total Quantity
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={updateForm.quantity}
                onChange={(e) =>
                  setUpdateForm({
                    ...updateForm,
                    quantity:
                      parseInt(e.target.value.replace(/^0+(?=\d)/, '')) || 1,
                  })
                }
                sx={purchaseFieldSx}
              />
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Sold Amount (£)
              </Typography>
              <TextField
                fullWidth
                value={updateForm.soldAmount}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9]/g, '');
                  value = value.replace(/^0+(?=\d)/, '');
                  setUpdateForm({
                    ...updateForm,
                    soldAmount: Number(value),
                  });
                }}
                sx={purchaseFieldSx}
              />
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Remarks
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter any remarks or notes"
                value={updateForm.remarks}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, remarks: e.target.value })
                }
                sx={purchaseFieldSx}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={purchaseDialogActionsSx}>
          <SecondaryButton title="Cancel" onClick={onClose} disabled={loading} />
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#7B1FA2' }} />
          ) : (
            <PrimaryButton title="Update Lead" onClick={handleSubmit} />
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default UpdateLeadDialog;
